import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, AlertTriangle, Settings, DollarSign, UserCheck, CheckCircle2 } from 'lucide-react';
export function RunRosterPage() {
  const steps = [{
    title: 'See the whole roster',
    desc: 'View every artist, campaign, and deal in one place.',
    icon: Users
  }, {
    title: 'Prioritize high-value work',
    desc: 'Surface opportunities that need action now.',
    icon: AlertTriangle
  }, {
    title: 'Standardize the workflow',
    desc: 'Use templates, approvals, and shared status rules.',
    icon: Settings
  }, {
    title: 'Keep finance in view',
    desc: 'Track invoices, deposits, and settlement without leaving the booking workflow.',
    icon: DollarSign
  }, {
    title: 'Give the team clear ownership',
    desc: 'Assignments, approvals, and inbox collaboration stay visible.',
    icon: UserCheck
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7385">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-20 text-center" data-id="element-7386">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7387">
          Run your roster like an operating system, not a spreadsheet.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed" data-id="element-7388">
          Track campaigns, opportunities, contracts, payments, and team
          responsibilities across multiple artists in one shared workflow.
        </motion.p>
      </section>

      {/* Problem Section */}
      <section className="max-w-4xl mx-auto px-6 mb-20" data-id="element-7389">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-8 md:p-12 text-center" data-id="element-7390">
          <h2 className="text-2xl font-bold text-textMain mb-4" data-id="element-7391">
            The more artists you manage, the more hidden risk compounds.
          </h2>
          <p className="text-textMuted leading-relaxed" data-id="element-7392">
            Open deals get buried, deposits slip, inboxes fragment, and route
            gaps stay invisible too long when the team is spread across email,
            docs, and spreadsheets.
          </p>
        </div>
      </section>

      {/* Scenario Story */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7393">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-7394">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-7395"></div>
          <div className="prose prose-invert max-w-none" data-id="element-7396">
            <p className="italic text-lg text-textMuted leading-relaxed mb-6" data-id="element-7397">
              An agency is juggling six artists, three active regional runs, a
              pile of open offers, and too many email threads to trust memory.
              One deposit is late, another deal is waiting on contract language,
              and a high-value route still has gaps.
            </p>
            <p className="italic text-lg text-textMuted leading-relaxed" data-id="element-7398">
              Booker gives the team a portfolio view of the whole roster — what
              is moving, what is blocked, who owns it, and where the most
              valuable opportunities are.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7399">
        <div className="space-y-6" data-id="element-7400">
          {steps.map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-6 bg-surface border border-border rounded-2xl p-6" data-id="element-7401">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-7402">
                <step.icon className="w-6 h-6 text-primary" data-id="element-7403" />
              </div>
              <div data-id="element-7404">
                <h3 className="text-xl font-bold text-textMain mb-2" data-id="element-7405">
                  Step {i + 1} — {step.title}
                </h3>
                <p className="text-textMuted" data-id="element-7406">{step.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7407">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center" data-id="element-7408">
          <h2 className="text-2xl font-bold text-textMain" data-id="element-7409">
            More control, less chaos, better throughput.
          </h2>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7410">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7411">
          <Link to="/demo" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7412">
            Book a demo
          </Link>
          <Link to="/contact-sales" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7413">
            Contact sales
          </Link>
        </div>
      </section>
    </div>;
}